import React, { useState, useMemo, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import OverlaySuiviCompta from "./Compta/OverlaySuiviCompta";
import FicheClientOverlay from "../../Portefeuille/FicheClientOverlay";
import { useClient } from "../../Portefeuille/useClients.js";
import { CiFilter } from "react-icons/ci";
import { IoIosSearch } from "react-icons/io";
import { missionCommencee, missionActive, buildMonthTasks, COMPTA_TASK_ORDER, estBloque, classeLigneBloquee } from "./suiviProdUtils";
import BadgeBloque from "./BadgeBloque";

function SuiviCompta({ suiviComptaData, loading, refetch }) {
  // 🔹 supprime nameFilter (inutile)
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [showOverlayDetails, setShowOverlayDetails] = useState(false);

  // Fiche client — même cache React Query partagé que Portefeuille.js
  // (["client", id] / ["clients"]) au lieu d'un fetch + cache local isolé :
  // ainsi une édition faite ici (via Update.js) se reflète instantanément
  // dans Portefeuille.js et vice-versa.
  const queryClient = useQueryClient();
  const [ficheClientOpen, setFicheClientOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const { data: ficheClientDetail, isLoading: ficheClientLoading } =
    useClient(selectedClientId);

  const showClientDetails = (client) => {
    setSelectedClientId(client.id);
    setFicheClientOpen(true);
  };

  // En attendant que useClient(selectedClientId) réponde, on affiche tout de
  // suite le snapshot déjà embarqué dans suiviComptaData (même principe que
  // Portefeuille.js : détail React Query prioritaire, fallback liste).
  const ficheClientFallback =
    suiviComptaData.find((c) => c.id === selectedClientId) || null;
  const ficheClientData = ficheClientDetail || ficheClientFallback;

  // Propage une édition (nom, groupement, collaborateur...) vers le cache
  // React Query partagé — même mécanique que handleUpdateUserInList dans
  // Portefeuille.js.
  const handleUpdateClientInList = (updatedClient) => {
    queryClient.setQueryData(["client", updatedClient.id], updatedClient);
    queryClient.setQueryData(["clients"], (prev) =>
      Array.isArray(prev)
        ? prev.map((c) => (c.id === updatedClient.id ? updatedClient : c))
        : prev
    );
  };

  const [showFilterOverlay, setShowFilterOverlay] = useState(false);

  // 🔹 garde uniquement filterRows
  const [filterRows, setFilterRows] = useState([
    { column: "collaborateur", selectedValues: [] },
  ]);

  const displayYear = selectedYear ? parseInt(selectedYear) : 2026;

  const overlayRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) {
        setShowFilterOverlay(false);
      }
    };

    if (showFilterOverlay) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterOverlay]);

  // Fonction qui ajoute une ligne de filtre (avec valeurs par défaut)
  const handleAddFilter = () => {
    setFilterRows((prev) => [...prev, { column: "collaborateur", text: "" }]);
  };

  // Fonction pour supprimer une ligne de filtre
  const handleRemoveFilter = (indexToRemove) => {
    setFilterRows((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleFilterChange = (index, field, value) => {
    setFilterRows((prev) =>
      prev.map((filter, i) =>
        i === index ? { ...filter, [field]: value, selectedValues: [] } : filter
      )
    );
  };

  const handleCheckboxValueChange = (index, value) => {
    setFilterRows((prev) =>
      prev.map((filter, i) => {
        if (i !== index) return filter;

        const selectedValues = Array.isArray(filter.selectedValues)
          ? filter.selectedValues
          : [];

        const selected = selectedValues.includes(value)
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value];

        return { ...filter, selectedValues: selected };
      })
    );
  };

  const uniqueValues = useMemo(() => {
    const values = {
      formeSociale: Array.from(
        new Set(suiviComptaData.flatMap((client) => client.formeSociale))
      ),
      regimeFiscal: Array.from(
        new Set(suiviComptaData.map((client) => client.regimeFiscal))
      ),
      regimeTVA: Array.from(
        new Set(suiviComptaData.map((client) => client.regimeTVA))
      ),
      expertComptable: Array.from(
        new Set(suiviComptaData.map((client) => client.expertComptable))
      ),
      superviseur: Array.from(
        new Set(suiviComptaData.map((client) => client.superviseur))
      ),
      collaborateur: Array.from(
        new Set(suiviComptaData.map((client) => client.collaborateur))
      ),
      missionComptable: Array.from(
        new Set(suiviComptaData.map((client) => client.missionComptable))
      ),
      missionSociale: Array.from(
        new Set(suiviComptaData.map((client) => client.missionSociale))
      ),
      missionJuridique: Array.from(
        new Set(suiviComptaData.map((client) => client.missionJuridique))
      ),
      missionIR: Array.from(
        new Set(suiviComptaData.map((client) => client.missionIR))
      ),
      groupement: Array.from(
        new Set(suiviComptaData.map((client) => client.groupement))
      ),
    };
    return values;
  }, [suiviComptaData]);

  const years = useMemo(() => {
    const yearSet = new Set(
      suiviComptaData.map((client) =>
        new Date(client.dateCloture).getFullYear()
      )
    );
    return Array.from(yearSet).sort((a, b) => b - a); // tri décroissant
  }, [suiviComptaData]);

  const handleResetFilters = () => {
    setFilterRows([{ column: "collaborateur", selectedValues: [] }]);
    setSearchTerm("");
    setSelectedYear("");
  };

  if (loading)
    return (
      <div>
        <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
          <div className="h-9 w-64 rounded-md bg-slate-200 animate-pulse" />
          <div className="flex items-center gap-5">
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
          </div>
        </div>
        <div className="mt-6 border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="text-sm min-w-[1200px] w-full">
            <thead>
              <tr className="bg-[#6e1a36]">
                <th className="px-3 py-2">
                  <div className="h-3 rounded bg-[#ffffff33] animate-pulse w-16" />
                </th>
                {Array.from({ length: 12 }).map((_, i) => (
                  <th key={i} className="px-3 py-2">
                    <div className="h-3 rounded bg-[#ffffff33] animate-pulse w-8 mx-auto" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-4 py-2">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-32" />
                  </td>
                  {Array.from({ length: 12 }).map((__, j) => (
                    <td key={j} className="px-3 py-2 text-center">
                      <div className="h-2 rounded-md bg-slate-200 animate-pulse mx-auto w-8" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

  if (!suiviComptaData || suiviComptaData.length === 0)
    return <p>Aucune donnée Compta disponible.</p>;

  // Filtrer les suiviComptaData selon le searchTerm et l'année sélectionnée
  const filteredData = suiviComptaData.filter((client) => {
    // 🔹 recherche texte
    const matchesSearch = client.nom
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    // 🔹 filtre année (>= date cloture)
    const clientYear = new Date(client.dateCloture).getFullYear();
    const matchesYear = selectedYear
      ? parseInt(selectedYear) >= clientYear
      : true;

    // 🔹 filtres dynamiques
    const matchesFilters = filterRows.every((filter) => {
      if (!filter.selectedValues || filter.selectedValues.length === 0)
        return true;
      return filter.selectedValues.includes(client[filter.column]);
    });

    // Mission pas encore débutée cette année-là → masqué ; client archivé
    // depuis une année antérieure → masqué aussi.
    const missionDebutee =
      missionCommencee(client, displayYear) &&
      missionActive(client, displayYear);

    return matchesSearch && matchesYear && matchesFilters && missionDebutee;
  });

  const getAppliedFilters = () => {
    return filterRows
      .map((filter) => {
        // Vérifier si selectedValues est un tableau et non vide
        if (
          Array.isArray(filter.selectedValues) &&
          filter.selectedValues.length > 0
        ) {
          const selectedValues = filter.selectedValues.join(", ");
          return `${filter.column}: ${selectedValues}`;
        }
        return null; // Si aucun filtre n'est sélectionné, on ne le retourne pas
      })
      .filter(Boolean) // Filtrer les null ou undefined
      .join(" - "); // Joindre les filtres avec un séparateur " | "
  };

  const progressColor = "green";

  const handleCellClick = (client, moisNumero) => {
    setSelectedClient(client);
    setSelectedMonth(moisNumero);
    setSelectedTask(null);
    setShowOverlayDetails(true);
  };

  const getMonthTasks = () =>
    buildMonthTasks(selectedClient, selectedMonth, displayYear);

  return (
    <div>

      <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
        <div className="relative flex-1 max-w-sm">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filtrer par nom du dossier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 w-full rounded-md bg-slate-50 border border-slate-200 text-sm focus:bg-white focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-5">
          <select
            value={selectedYear || "2026"} // par défaut 2026
            onChange={(e) => setSelectedYear(e.target.value)}
            className="border border-slate-300 rounded-md 
                                    h-9 px-2 text-xs leading-tight w-20
                                    bg-white"
          >
            {[
              2026, // Toujours mettre 2026 en premier
              ...years.filter((year) => year !== 2026), // Supprimer le doublon
            ]
              .sort((a, b) => b - a) // tri décroissant
              .map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
          </select>
          <button
            onClick={() => setShowFilterOverlay(!showFilterOverlay)}
            className={`flex items-center gap-2 h-9 px-3 rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
          >
            <CiFilter className="w-3 h-3" />
            Filtrer
          </button>
        </div>
        {showFilterOverlay && (
          <div
            ref={overlayRef}
            className="fixed top-[40%] left-[65%] -translate-x-1/2 bg-white border border-slate-200 rounded-lg p-4 shadow-lg z-50 w-2/5 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between mb-2">
              <p className="font-semibold">Filtres avancés</p>
              <button
                className="text-sm text-slate-600 hover:text-slate-800"
                onClick={handleResetFilters}
              >
                Réinitialiser
              </button>
            </div>

            {filterRows.map((filter, index) => (
              <div key={index} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <select
                    value={filter.column}
                    onChange={(e) =>
                      handleFilterChange(index, "column", e.target.value)
                    }
                    className="border border-slate-200 rounded-md p-1 text-sm"
                  >
                    {Object.keys(uniqueValues).map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveFilter(index)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    - Supprimer
                  </button>
                </div>
                <div className="flex flex-col ml-2">
                  {uniqueValues[filter.column]?.map((value) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={filter.selectedValues.includes(value)}
                        onChange={() => handleCheckboxValueChange(index, value)}
                      />
                      {value || "(vide)"}
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <button
              className="text-sm text-slate-700 hover:text-slate-900 mt-2"
              onClick={handleAddFilter}
            >
              + Ajouter filtre
            </button>
          </div>
        )}
      </div>
      {getAppliedFilters() && (
        <p className="m-0 mt-4 text-xs text-slate-700">
          <strong>Filtres appliqués :</strong> {getAppliedFilters()}
        </p>
      )}

      <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 250px)" }}
        >
          <table
            className="w-full min-w-[1200px]"
            style={{ fontSize: "0.72rem" }}
          >
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#6e1a36] text-xs">
                <th className="px-2 py-3.5 text-white font-semibold text-left">
                  Dossier
                </th>
                {Array.from({ length: 12 }, (_, i) => {
                  // Exemple : 2025 => 25, 2026 => 26, sinon 26 par défaut
                  const total = selectedYear
                    ? parseInt(selectedYear.slice(-2)) // si une année est sélectionnée
                    : 26; // par défaut 26 pour "Toutes les années"

                  return (
                    <th
                      className="px-2 py-3.5 text-white font-semibold text-center"
                      key={i}
                    >{`${i + 1}/${total}`}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.map((client) => (
                <tr
                  key={client.id}
                  className={`hover:bg-slate-50/50 transition-colors ${classeLigneBloquee(client)}`}
                >
                  <td
                    onClick={() => showClientDetails(client)}
                    className="px-2 py-2 font-medium text-slate-900 cursor-pointer hover:text-[#9B1B5D] transition-colors"
                  >
                    {client.nom}
                    {estBloque(client) && <BadgeBloque />}
                  </td>
                  {Array.from({ length: 12 }, (_, i) => {
                    const mois = client.Compta[0]?.mois.find(
                      (m) => m.numero === i + 1 && m.annee === displayYear
                    );

                    const order = COMPTA_TASK_ORDER;

                    const tachesCompletes = order.filter((type) =>
                      mois?.taches?.some(
                        (t) => t.type === type && t.statut === true
                      )
                    ).length;

                    const totalTaches = order.length;
                    const progressPercent =
                      (tachesCompletes / totalTaches) * 100;

                    const isEmpty = tachesCompletes === 0;

                    return (
                      <td
                        key={i}
                        onClick={() => handleCellClick(client, i + 1)}
                        className="px-2 py-1.5 text-center cursor-pointer"
                      >
                        <div
                          className={`
           h-2 mx-auto rounded-md border overflow-hidden
          transition-all duration-200
          ${
            isEmpty
              ? "bg-slate-50 border border-slate-300 hover:bg-slate-100"
              : "bg-slate-100 border-slate-200"
          }
        `}
                        >
                          <div
                            className="h-full transition-all duration-300"
                            style={{
                              width: `${progressPercent}%`,
                              backgroundColor: progressColor,
                            }}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {filteredData.length === 0 && (
                <tr>
                  <td
                    colSpan={13}
                    className="text-center py-12 text-sm text-slate-500"
                  >
                    Aucun client trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showOverlayDetails && (
        <OverlaySuiviCompta
          selectedClient={selectedClient}
          selectedMonth={selectedMonth}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
          setShowOverlay={setShowOverlayDetails}
          getMonthTasks={getMonthTasks}
          selectedYear={displayYear} // <-- nouvelle prop
          refetch={refetch}
        />
      )}

      <FicheClientOverlay
        client={ficheClientData}
        open={ficheClientOpen}
        loading={ficheClientLoading}
        initialTab="general"
        onClose={() => setFicheClientOpen(false)}
        onClientUpdated={handleUpdateClientInList}
      />
    </div>
  );
}

export default SuiviCompta;
